import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { useTimezoneFormatter } from '@/hooks/useTimezoneFormatter';

export interface FlowRunReport {
    id: string;
    startTime: string;
    endTime: string;
    status: 'Running' | 'Success' | 'Failed' | 'Cancelled';
    totalDuration: string;
    nodes: {
        nodeId: string;
        nodeName: string;
        type: string;
        startTime: string;
        endTime?: string;
        duration?: string;
        rows?: number;
        status: 'Pending' | 'Running' | 'Success' | 'Failed';
        error?: string;
    }[];
}

interface FlowRunReportModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    report: FlowRunReport | null;
}

export default function FlowRunReportModal({ open, onOpenChange, report }: FlowRunReportModalProps) {
    const { formatDate } = useTimezoneFormatter();
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Flow Execution Report</DialogTitle>
                </DialogHeader>
                {report ? (
                    <div className="space-y-4">
                        <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                            <div>
                                <div className="text-xs text-gray-500 uppercase">Status</div>
                                <div className={`font-medium ${report.status === 'Success' ? 'text-green-600' : report.status === 'Failed' ? 'text-red-600' : 'text-blue-600'}`}>{report.status}</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 uppercase">Start Time</div>
                                <div className="font-medium text-gray-900">{formatDate(report.startTime)}</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 uppercase">End Time</div>
                                <div className="font-medium text-gray-900">{formatDate(report.endTime)}</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 uppercase">Nodes</div>
                                <div className="font-medium text-gray-900">{report.nodes.length}</div>
                            </div>
                        </div>

                        <div className="border rounded-lg overflow-hidden">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="px-4 py-2 font-medium text-gray-500">Node Name</th>
                                        <th className="px-4 py-2 font-medium text-gray-500">Type</th>
                                        <th className="px-4 py-2 font-medium text-gray-500">Duration</th>
                                        <th className="px-4 py-2 font-medium text-gray-500">Rows</th>
                                        <th className="px-4 py-2 font-medium text-gray-500">Status</th>
                                        <th className="px-4 py-2 font-medium text-gray-500">Error</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {report.nodes
                                        .filter(n => n.type !== 'annotation')
                                        .sort((a, b) => {
                                            const tA = a.startTime ? new Date(a.startTime).getTime() : 0;
                                            const tB = b.startTime ? new Date(b.startTime).getTime() : 0;
                                            return tA - tB;
                                        })
                                        .map((n, i) => (
                                            <tr key={i} className="hover:bg-gray-50">
                                                <td className="px-4 py-2 font-medium text-gray-900">{n.nodeName}</td>
                                                <td className="px-4 py-2 text-gray-500 text-xs uppercase">{n.type}</td>
                                                <td className="px-4 py-2 text-gray-600 text-xs">
                                                    {n.startTime && n.endTime ? (
                                                        `${formatDate(n.startTime)} - ${formatDate(n.endTime)}`
                                                    ) : '-'}
                                                </td>
                                                <td className="px-4 py-2 text-gray-600">{n.rows?.toLocaleString() || '-'}</td>
                                                <td className="px-4 py-2">
                                                    <span className={`px-2 py-0.5 rounded text-xs ${n.status === 'Success' ? 'bg-green-100 text-green-700' :
                                                        n.status === 'Failed' ? 'bg-red-100 text-red-700' :
                                                            'bg-blue-100 text-blue-700'
                                                        }`}>
                                                        {n.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 text-red-600 text-xs truncate max-w-[200px]" title={n.error}>
                                                    {n.error || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">No report available.</div>
                )}
                <DialogFooter>
                    <Button onClick={() => onOpenChange(false)}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
